import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { SpotCheckDialog } from './SpotCheckDialog';
import { ReadinessBadge } from './SpotCheckSummary';

const STYLES = ['flat', 'hobbled', 'relaxed', 'cascade', 'aventura', 'pleated'];
export const CATEGORY_LABELS = {
  flat_face: 'Flat face', lined_face: 'Lined face', weak_pleats: 'Weak pleats', wrong_stack: 'Wrong stack',
  swag_look: 'Swag look', uneven_folds: 'Uneven folds', generation_defect: 'Generation defect', other: 'Other',
};
const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/* Style-consistency QC: run (never approves), progress, pass/fail/borderline per style, contact sheets */
export const SceneQcPanel = ({ api, headers, onDone, onPickQc, onPickCategory, onLogged, refreshKey }) => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => Promise.all([axios.get(`${api}/qc/status`, { headers }), axios.get(`${api}/qc/redo-estimate`, { headers }), axios.get(`${api}/qc/spot-check/summary`, { headers })])
    .then(([a, b, c]) => setSt({ ...a.data, est: b.data, spot: c.data })).catch(() => {}), [api, headers]);
  const running = ['running', 'building_sheets'].includes(st?.run?.status);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);
  useEffect(() => { if (st?.run?.status === 'completed') onDone?.(); }, [st?.run?.status, st?.run?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    try {
      const { data } = await axios.post(`${api}/qc/run`, { force: false }, { headers });
      toast.success(data.total ? `QC started for ${data.total} scenes` : 'All pending scenes already QC-checked — rebuilding sheets');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start QC'); }
  };

  const redo = async (style, n) => {
    const est = (n * ((st?.est?.cost_per_image_usd ?? 0.04) + (st?.est?.qc_cost_per_call_usd ?? 0.01))).toFixed(2);
    if (!window.confirm(`Regenerate ${n} QC-failed ${style} scenes with the approved ${style} reference?\n\n${n} image calls + ${n} auto-QC checks ≈ $${est}.\nPassing and borderline scenes are not touched; nothing goes live.`)) return;
    try {
      const { data } = await axios.post(`${api}/qc/redo-failures`, { style }, { headers });
      toast.success(`Redoing ${data.count} ${style} failures — each new scene is auto-QC'd and stays pending`);
      load(); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start redo'); }
  };

  const sum = st?.summary || {};
  const r = st?.run;
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-2" data-testid="scene-qc-panel">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-neutral-700" />
        <span className="text-sm font-medium text-neutral-900">Style QC</span>
        <span className="text-[11px] text-neutral-500">Compares each pending scene with its approved reference. Never approves or replaces anything.</span>
        <Button size="sm" className="h-7 text-[11px] ml-auto gap-1" disabled={running} onClick={run} data-testid="scene-qc-run-btn">
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}{running ? 'QC running…' : 'Run QC on unchecked'}
        </Button>
      </div>
      {r && (
        <p className="text-[11px] text-neutral-600" data-testid="scene-qc-progress">
          {r.kind === 'categorize' ? 'Last run: failure categories' : 'Last run'}: {r.status.replace('_', ' ')} · {r.total ? `${r.done}/${r.total} checked` : 'contact sheets rebuilt (no new scenes to check)'}{r.errors ? ` · ${r.errors} errors` : ''}
        </p>
      )}
      {st?.est?.image_calls > 0 && (
        <p className="text-[11px] text-neutral-600" data-testid="scene-qc-redo-estimate">
          Redo all failures: {st.est.image_calls} image calls + {st.est.qc_calls} auto-QC checks ≈ ${st.est.estimated_total_usd} (~{st.est.estimated_minutes} min per style queue)
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {STYLES.map(s => {
          const c = sum[s] || {};
          const sheet = r?.sheets?.[s];
          return (
            <div key={s} className="rounded-md bg-white border border-neutral-200 p-2 text-[11px]" data-testid={`scene-qc-style-${s}`}>
              <div className="flex items-center justify-between gap-1">
                <p className="font-semibold capitalize text-neutral-900">{s}</p>
                {st?.spot && <ReadinessBadge v={st.spot.styles[s]} style={`card-${s}`} />}
              </div>
              <div className="flex gap-2 mt-1">
                <button className="text-green-700" onClick={() => onPickQc(s, 'pass')} data-testid={`scene-qc-${s}-pass`}>{c.pass || 0} pass</button>
                <button className="text-red-700" onClick={() => onPickQc(s, 'fail')} data-testid={`scene-qc-${s}-fail`}>{c.fail || 0} fail</button>
                <button className="text-amber-700" onClick={() => onPickQc(s, 'borderline')} data-testid={`scene-qc-${s}-borderline`}>{c.borderline || 0} border</button>
              </div>
              {Object.keys(c.categories || {}).length > 0 && (
                <div className="mt-1 space-y-0.5" data-testid={`scene-qc-categories-${s}`}>
                  {Object.entries(c.categories).map(([cat, v]) => (
                    <button key={cat} className="block text-left text-neutral-600 hover:underline" onClick={() => onPickCategory(s, cat)} data-testid={`scene-qc-cat-${s}-${cat}`}>
                      {CATEGORY_LABELS[cat] || cat}: {v.fail ? <span className="text-red-700">{v.fail} fail</span> : null}{v.fail && v.borderline ? ' · ' : ''}{v.borderline ? <span className="text-amber-700">{v.borderline} border</span> : null}
                    </button>
                  ))}
                </div>
              )}
              {c.unchecked ? <p className="text-neutral-400 mt-0.5">{c.unchecked} unchecked</p> : null}
              {c.fail > 0 && (
                <button className="mt-1 block text-red-700 hover:underline font-medium disabled:opacity-50" disabled={running} onClick={() => redo(s, c.fail)} data-testid={`scene-qc-redo-${s}`}>
                  Redo {c.fail} failure{c.fail > 1 ? 's' : ''}
                </button>
              )}
              {sheet && (
                <a href={abs(sheet)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid={`scene-qc-sheet-${s}`}>
                  <ImageIcon className="w-3 h-3" />Contact sheet
                </a>
              )}
              {c.pass > 0 && <div><SpotCheckDialog api={api} headers={headers} style={s} onLogged={onLogged} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
